import React, { useRef, useState } from "react";
import DatePicker from "../../../../../components/inputs/MomentDatePicker";
import {
  Button,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Popconfirm,
  Select,
  Table,
  Tag,
  Tooltip,
  Upload,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  CheckOutlined,
  CloseOutlined,
  PlusOutlined,
  UploadOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import { saveAs } from "file-saver";
import axios from "axios";
import moment from "moment";
import { useQuery, useQueryClient } from "react-query";
import { useSelector } from "react-redux";
import { serverUrl } from "../../../../../nestserver";
import { selectUser } from "../../../../../redux/slices/auth.slice";
import { uploadToS3 } from "../../../../../services/upload.service";

const { Option } = Select;
const { RangePicker } = DatePicker;

const ExpensesMain = () => {
  const user = useSelector(selectUser);
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [rejectForm] = Form.useForm();
  const amountInputRef = useRef(null);

  // Filters
  const [searchName, setSearchName] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [dateRange, setDateRange] = useState([]);

  // Modals & States
  const [isFormModalVisible, setIsFormModalVisible] = useState(false);
  const [isRejectModalVisible, setIsRejectModalVisible] = useState(false);
  const [isReceiptModalVisible, setIsReceiptModalVisible] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [rejectingExpenseId, setRejectingExpenseId] = useState(null);
  const [previewReceiptUrl, setPreviewReceiptUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadedReceipt, setUploadedReceipt] = useState(null);
  const [exporting, setExporting] = useState(false);

  const categoriesList = ["Supplies", "Maintenance", "Utility", "Events", "Food & Beverage", "Salary", "Other"];

  const fetchExpenses = async () => {
    const params = {};
    if (searchName) params.name = searchName;
    if (statusFilter && statusFilter !== "all") params.status = statusFilter;
    if (categoryFilter && categoryFilter !== "all") params.category = categoryFilter;
    if (dateRange && dateRange.length === 2) {
      params.startDate = dateRange[0].format("YYYY-MM-DD");
      params.endDate = dateRange[1].format("YYYY-MM-DD");
    }

    const res = await axios.get(`${serverUrl}/expenses`, {
      params,
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
      },
    });
    return res.data?.result || [];
  };

  const { data: expenses = [], isLoading } = useQuery(
    ["expenses", searchName, statusFilter, categoryFilter, dateRange],
    fetchExpenses
  );

  const handleExportExcel = async () => {
    try {
      setExporting(true);
      const params = {};
      if (searchName) params.name = searchName;
      if (statusFilter && statusFilter !== "all") params.status = statusFilter;
      if (categoryFilter && categoryFilter !== "all") params.category = categoryFilter;
      if (dateRange && dateRange.length === 2) {
        params.startDate = dateRange[0].format("YYYY-MM-DD");
        params.endDate = dateRange[1].format("YYYY-MM-DD");
      }

      const response = await axios({
        url: `${serverUrl}/expenses/export`,
        method: "GET",
        params,
        responseType: "blob",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
        },
      });

      saveAs(response.data, `expenses-report-${moment().format("YYYY-MM-DD")}.xlsx`);
      message.success("Expenses report exported successfully!");
    } catch (error) {
      console.error("Export failed:", error);
      message.error("Failed to export expenses report.");
    } finally {
      setExporting(false);
    }
  };

  const handleUploadReceipt = async ({ file, onSuccess, onError }) => {
    try {
      setUploading(true);
      const res = await uploadToS3(file, "expenses");
      const receiptData = {
        id: res.id,
        name: res.files.name,
        url: res.files.original,
      };
      setUploadedReceipt(receiptData);
      message.success("Receipt uploaded successfully!");
      onSuccess(null, file);
    } catch (err) {
      console.error(err);
      message.error("Failed to upload receipt file.");
      onError(err);
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (values) => {
    try {
      const payload = {
        name: values.name,
        amount: values.amount,
        category: values.category === "custom" ? values.customCategory : values.category,
        description: values.description,
        date: values.date ? values.date.toDate() : new Date(),
        receipt: uploadedReceipt || undefined,
      };

      if (editingExpense) {
        await axios.patch(`${serverUrl}/expenses/${editingExpense._id}`, payload, {
          headers: {
            Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
          },
        });
        message.success("Expense updated successfully!");
      } else {
        await axios.post(`${serverUrl}/expenses`, payload, {
          headers: {
            Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
          },
        });
        message.success("Expense created successfully!");
      }

      setIsFormModalVisible(false);
      form.resetFields();
      setEditingExpense(null);
      setUploadedReceipt(null);
      queryClient.invalidateQueries("expenses");
    } catch (err) {
      const errMsg = err?.response?.data?.error?.errors?.[0] || "Failed to save expense.";
      message.error(errMsg);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${serverUrl}/expenses/${id}`, {
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
        },
      });
      message.success("Expense deleted successfully!");
      queryClient.invalidateQueries("expenses");
    } catch (err) {
      const errMsg = err?.response?.data?.error?.errors?.[0] || "Failed to delete expense.";
      message.error(errMsg);
    }
  };

  const handleApprove = async (id) => {
    try {
      await axios.patch(
        `${serverUrl}/expenses/${id}/approve`,
        {},
        {
          headers: {
            Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
          },
        }
      );
      message.success("Expense approved successfully!");
      queryClient.invalidateQueries("expenses");
    } catch (err) {
      const errMsg = err?.response?.data?.error?.errors?.[0] || "Failed to approve expense.";
      message.error(errMsg);
    }
  };

  const handleReject = async (values) => {
    try {
      await axios.patch(
        `${serverUrl}/expenses/${rejectingExpenseId}/reject`,
        { rejection_reason: values.rejection_reason },
        {
          headers: {
            Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
          },
        }
      );
      message.success("Expense request rejected.");
      setIsRejectModalVisible(false);
      rejectForm.resetFields();
      setRejectingExpenseId(null);
      queryClient.invalidateQueries("expenses");
    } catch (err) {
      const errMsg = err?.response?.data?.error?.errors?.[0] || "Failed to reject expense.";
      message.error(errMsg);
    }
  };

  const openAddModal = () => {
    setEditingExpense(null);
    setUploadedReceipt(null);
    form.resetFields();
    setIsFormModalVisible(true);
  };

  const openEditModal = (record) => {
    setEditingExpense(record);
    setUploadedReceipt(record.receipt || null);
    form.setFieldsValue({
      name: record.name,
      amount: record.amount,
      category: categoriesList.includes(record.category) ? record.category : "custom",
      customCategory: categoriesList.includes(record.category) ? "" : record.category,
      description: record.description,
      date: record.date ? moment(record.date) : null,
    });
    setIsFormModalVisible(true);
  };

  const showReceiptPreview = (url) => {
    setPreviewReceiptUrl(url);
    setIsReceiptModalVisible(true);
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      className: "font-semibold capitalize text-slate-800",
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      className: "font-bold text-emerald-600",
      render: (amt) => `₹${Number(amt).toLocaleString()}`,
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      render: (cat) => <Tag color="blue">{cat || "General"}</Tag>,
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      render: (d) => moment(d).format("DD-MM-YYYY"),
    },
    {
      title: "Created By",
      key: "created_by",
      render: (record) => {
        const creator = record?.created_by;
        let details = "—";
        if (creator) {
          const profile = creator.admin || creator.teacher;
          const fullName = profile
            ? `${profile.first_name || ""} ${profile.last_name || ""}`.trim()
            : creator.username;
          const role = creator.role === "admin" ? "Admin" : "Instructor";
          details = `${fullName} (${role})`;
        }
        return <span className="text-slate-600 text-sm">{details}</span>;
      },
    },
    {
      title: "Receipt",
      dataIndex: "receipt",
      key: "receipt",
      align: "center",
      render: (receipt) => {
        if (receipt?.url) {
          return (
            <Tooltip title="View Receipt">
              <Button
                type="text"
                icon={<EyeOutlined className="text-blue-500 text-lg" />}
                onClick={() => showReceiptPreview(receipt.url)}
              />
            </Tooltip>
          );
        }
        return <span className="text-slate-400 text-xs">—</span>;
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      align: "center",
      render: (status, record) => {
        let color = "orange";
        if (status === "approved") color = "green";
        if (status === "rejected") color = "red";
        return (
          <div className="flex flex-col items-center">
            <Tag color={color} className="capitalize font-semibold px-2 py-0.5">
              {status}
            </Tag>
            {status === "rejected" && record.rejection_reason && (
              <span className="text-xs text-red-500 mt-1 max-w-150 truncate block">
                Reason: {record.rejection_reason}
              </span>
            )}
          </div>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      render: (record) => {
        const isPending = record.status === "pending";
        const isAdminApprover = user?.role === "admin" && user?.admin?.can_approve_transaction;

        return (
          <div className="flex items-center space-x-2 justify-center">
            {isPending && (
              <>
                <Tooltip title="Edit">
                  <Button
                    type="primary"
                    shape="circle"
                    size="small"
                    className="flex items-center justify-center bg-blue-500 border-blue-500"
                    icon={<EditOutlined />}
                    onClick={() => openEditModal(record)}
                  />
                </Tooltip>
                <Popconfirm
                  title="Are you sure you want to delete this expense?"
                  onConfirm={() => handleDelete(record._id)}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button
                    type="primary"
                    danger
                    shape="circle"
                    size="small"
                    className="flex items-center justify-center"
                    icon={<DeleteOutlined />}
                  />
                </Popconfirm>
              </>
            )}

            {isPending && isAdminApprover && (
              <>
                <Tooltip title="Approve">
                  <Button
                    type="primary"
                    shape="circle"
                    size="small"
                    className="flex items-center justify-center bg-emerald-500 border-emerald-500 hover:bg-emerald-600 hover:border-emerald-600"
                    icon={<CheckOutlined />}
                    onClick={() => handleApprove(record._id)}
                  />
                </Tooltip>
                <Tooltip title="Reject">
                  <Button
                    type="primary"
                    danger
                    shape="circle"
                    size="small"
                    className="flex items-center justify-center bg-red-500 border-red-500 hover:bg-red-600 hover:border-red-600"
                    icon={<CloseOutlined />}
                    onClick={() => {
                      setRejectingExpenseId(record._id);
                      setIsRejectModalVisible(true);
                    }}
                  />
                </Tooltip>
              </>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div className="pt-4">
      <div
        style={{ borderRadius: "10px", color: "#616161" }}
        className="bg-white drop-shadow-md p-8 w-full"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b pb-5">
          <div className="text-2xl text-orange-500 font-bold">
            Expenses Management
          </div>
          <div className="flex flex-wrap gap-3">
            <Button
              type="default"
              onClick={handleExportExcel}
              loading={exporting}
              className="h-10 border-orange-500 text-orange-500 hover:border-orange-600 hover:text-orange-600 font-semibold mb-2"
              style={{ display: "inline-flex", alignItems: "center" }}
            >
              <DownloadOutlined />
              <span>Export to Excel</span>
            </Button>
            <Button
              type="primary"
              className="h-10 bg-orange-500 border-orange-500 hover:bg-orange-600 hover:border-orange-600 font-semibold mb-2"
              onClick={openAddModal}
              style={{ display: "inline-flex", alignItems: "center" }}
            >
              <PlusOutlined />
              <span>Add Expense</span>
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap md:flex-nowrap items-end gap-4 py-6 border-b mb-6">
          <div className="flex flex-col flex-1 min-w-50">
            <label className="text-sm pb-1.5 font-semibold text-slate-500">Search Name</label>
            <Input
              placeholder="Search expense name..."
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
              allowClear
            />
          </div>
          <div className="flex flex-col flex-1 min-w-37.5">
            <label className="text-sm pb-1.5 font-semibold text-slate-500">Category</label>
            <Select
              value={categoryFilter}
              onChange={(val) => setCategoryFilter(val)}
              className="w-full"
            >
              <Option value="all">All Categories</Option>
              {categoriesList.map((cat) => (
                <Option key={cat} value={cat}>
                  {cat}
                </Option>
              ))}
            </Select>
          </div>
          <div className="flex flex-col flex-1 min-w-37.5">
            <label className="text-sm pb-1.5 font-semibold text-slate-500">Status</label>
            <Select
              value={statusFilter}
              onChange={(val) => setStatusFilter(val)}
              className="w-full"
            >
              <Option value="all">All Statuses</Option>
              <Option value="pending">Pending</Option>
              <Option value="approved">Approved</Option>
              <Option value="rejected">Rejected</Option>
            </Select>
          </div>
          <div className="flex flex-col flex-1 min-w-62.5">
            <label className="text-sm pb-1.5 font-semibold text-slate-500">Date Range</label>
            <RangePicker
              onChange={(dates) => setDateRange(dates || [])}
              className="w-full"
            />
          </div>
        </div>

        {/* Table */}
        <Table
          columns={columns}
          dataSource={expenses}
          rowKey="_id"
          loading={isLoading}
          pagination={{ pageSize: 10 }}
          className="border rounded-lg overflow-hidden"
        />

        {/* Form Modal (Add / Edit) */}
        <Modal
          title={editingExpense ? "Edit Expense" : "Add New Expense"}
          visible={isFormModalVisible}
          onCancel={() => {
            setIsFormModalVisible(false);
            setEditingExpense(null);
            setUploadedReceipt(null);
            form.resetFields();
          }}
          footer={null}
          width={600}
          destroyOnClose
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{ category: "Supplies", date: moment() }}
          >
            <Form.Item
              name="name"
              label="Expense Name"
              rules={[{ required: true, message: "Please enter the expense name" }]}
            >
              <Input placeholder="e.g. Science Lab Beakers" />
            </Form.Item>

            <div className="grid grid-cols-2 gap-4">
              <Form.Item
                name="amount"
                label="Amount (₹)"
                rules={[
                  { required: true, message: "Please enter the amount" },
                  { type: "number", min: 1, message: "Amount must be a positive number" },
                ]}
              >
                <InputNumber
                  ref={amountInputRef}
                  className="w-full"
                  placeholder="e.g. 2500"
                  formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                  parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
                  onWheel={() => amountInputRef.current?.blur()}
                />
              </Form.Item>

              <Form.Item
                name="date"
                label="Expense Date"
                rules={[{ required: true, message: "Please select the date" }]}
              >
                <DatePicker className="w-full" format="DD-MM-YYYY" />
              </Form.Item>
            </div>

            <Form.Item
              name="category"
              label="Category"
              rules={[{ required: true, message: "Please select a category" }]}
            >
              <Select>
                {categoriesList.map((cat) => (
                  <Option key={cat} value={cat}>
                    {cat}
                  </Option>
                ))}
                <Option value="custom">Custom Category</Option>
              </Select>
            </Form.Item>

            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) => prevValues.category !== currentValues.category}
            >
              {({ getFieldValue }) =>
                getFieldValue("category") === "custom" ? (
                  <Form.Item
                    name="customCategory"
                    label="Custom Category Name"
                    rules={[{ required: true, message: "Please enter custom category name" }]}
                  >
                    <Input placeholder="e.g. Sports Day Equipment" />
                  </Form.Item>
                ) : null
              }
            </Form.Item>

            <Form.Item name="description" label="Description / Notes">
              <Input.TextArea rows={3} placeholder="Provide details about this expense" />
            </Form.Item>

            <Form.Item label="Receipt File Upload">
              <div className="flex flex-wrap items-center gap-4">
                <Upload
                  customRequest={handleUploadReceipt}
                  maxCount={1}
                  showUploadList={false}
                  accept="image/*,application/pdf"
                >
                  <Button 
                    loading={uploading}
                    className="mb-2"
                    style={{ display: "inline-flex", alignItems: "center" }}
                  >
                    {!uploading && <UploadOutlined />}
                    <span>{uploading ? "Uploading..." : "Upload Receipt"}</span>
                  </Button>
                </Upload>
                {uploadedReceipt?.url && (
                  <div className="flex items-center space-x-2">
                    <span className="text-xs text-slate-500 truncate max-w-150">
                      {uploadedReceipt.name}
                    </span>
                    <Button
                      type="link"
                      size="small"
                      onClick={() => showReceiptPreview(uploadedReceipt.url)}
                    >
                      Preview
                    </Button>
                  </div>
                )}
              </div>
            </Form.Item>

            <div className="flex justify-end space-x-2 pt-4 border-t">
              <Button
                onClick={() => {
                  setIsFormModalVisible(false);
                  setEditingExpense(null);
                  setUploadedReceipt(null);
                  form.resetFields();
                }}
              >
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" className="bg-blue-500 border-blue-500">
                Save
              </Button>
            </div>
          </Form>
        </Modal>

        {/* Reject Rejection Reason Modal */}
        <Modal
          title="Reject Expense Request"
          visible={isRejectModalVisible}
          onCancel={() => {
            setIsRejectModalVisible(false);
            setRejectingExpenseId(null);
            rejectForm.resetFields();
          }}
          footer={null}
          width={400}
        >
          <Form form={rejectForm} layout="vertical" onFinish={handleReject}>
            <Form.Item
              name="rejection_reason"
              label="Reason for Rejection"
              rules={[{ required: true, message: "Please enter the rejection reason" }]}
            >
              <Input.TextArea rows={4} placeholder="State why this expense request is rejected..." />
            </Form.Item>
            <div className="flex justify-end space-x-2 pt-4">
              <Button
                onClick={() => {
                  setIsRejectModalVisible(false);
                  setRejectingExpenseId(null);
                  rejectForm.resetFields();
                }}
              >
                Cancel
              </Button>
              <Button type="primary" danger htmlType="submit">
                Reject Expense
              </Button>
            </div>
          </Form>
        </Modal>

        {/* Receipt Preview Modal */}
        <Modal
          title="Receipt Preview"
          visible={isReceiptModalVisible}
          onCancel={() => {
            setIsReceiptModalVisible(false);
            setPreviewReceiptUrl("");
          }}
          footer={null}
          width={700}
          bodyStyle={{ textAlign: "center" }}
        >
          {previewReceiptUrl.endsWith(".pdf") ? (
            <iframe
              src={previewReceiptUrl}
              title="Receipt PDF"
              width="100%"
              height="500px"
              style={{ border: "none" }}
            />
          ) : (
            <img
              src={previewReceiptUrl}
              alt="Receipt Preview"
              style={{ maxWidth: "100%", maxHeight: "550px", display: "inline-block" }}
            />
          )}
        </Modal>
      </div>
    </div>
  );
};

export default ExpensesMain;
